import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  NotificationChannel,
  NotificationDelivery,
  DeliveryStatus,
} from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type {
  CreateChannelInput,
  UpdateChannelInput,
} from '../api/notificationsClient.js';
import { useNotificationsClient } from '../hooks/useNotificationsClient.js';

/**
 * Integrations / Notifications (M4, §21): configure an ntfy notification channel, send a test
 * notification, toggle per-event enablement, enable/disable the channel, and inspect delivery
 * health with a replay action for failed deliveries.
 *
 * §21.2 security: the ntfy topic and token are secrets. They are write-only inputs — never returned
 * by the API and never rendered here. The form clears both after every submit so no secret lingers
 * in component state. Channel identity is shown via `safeConfig` (host + topic fingerprint) only.
 */

/** Configurable per-event enablement keys (§21.3). */
const EVENT_TYPES: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'song_request_group', label: 'New song-request group' },
  { key: 'freeform_suggestion', label: 'New free-form suggestion' },
  { key: 'improv_brief_created', label: 'New improv brief' },
  { key: 'improv_brief_selected', label: 'Improv brief selected' },
];

interface ChannelDraft {
  channelId: string | null;
  name: string;
  baseUrl: string;
  /** Secret (write-only). Cleared after submit; never populated from a fetched channel. */
  topic: string;
  /** Secret (write-only). Cleared after submit; never populated from a fetched channel. */
  token: string;
  eventPolicy: Record<string, boolean>;
}

function emptyPolicy(): Record<string, boolean> {
  const p: Record<string, boolean> = {};
  for (const e of EVENT_TYPES) p[e.key] = false;
  return p;
}

const EMPTY_DRAFT: ChannelDraft = {
  channelId: null,
  name: '',
  baseUrl: 'https://ntfy.sh',
  topic: '',
  token: '',
  eventPolicy: emptyPolicy(),
};

/** Read a channel's stored policy into a boolean map (contracts stores it as `unknown`). */
function policyFromChannel(channel: NotificationChannel): Record<string, boolean> {
  const p = emptyPolicy();
  for (const e of EVENT_TYPES) p[e.key] = Boolean(channel.eventPolicy[e.key]);
  return p;
}

const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  dead_letter: 'Dead letter',
};

function isReplayable(status: DeliveryStatus): boolean {
  return status === 'failed' || status === 'dead_letter';
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function IntegrationsPage() {
  const { artistId = '' } = useParams();
  const client = useNotificationsClient();

  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [deliveries, setDeliveries] = useState<NotificationDelivery[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState<ChannelDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyChannelId, setBusyChannelId] = useState<string | null>(null);
  const [busyDeliveryId, setBusyDeliveryId] = useState<string | null>(null);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setChannels(await client.listChannels(artistId));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load channels.');
    } finally {
      setLoading(false);
    }
  }, [client, artistId]);

  const loadDeliveries = useCallback(async () => {
    try {
      setDeliveries(await client.listDeliveries());
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load deliveries.');
    }
  }, [client]);

  useEffect(() => {
    void loadChannels();
    void loadDeliveries();
  }, [loadChannels, loadDeliveries]);

  const resetForm = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  }, []);

  const editChannel = useCallback((channel: NotificationChannel) => {
    // Secrets (topic/token) are never returned, so they start blank on edit (§21.2).
    setDraft({
      channelId: channel.channelId,
      name: channel.name,
      baseUrl: channel.safeConfig.host ?? 'https://ntfy.sh',
      topic: '',
      token: '',
      eventPolicy: policyFromChannel(channel),
    });
    setFormError(null);
    setNotice(null);
  }, []);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!draft.name.trim()) return;
      setSaving(true);
      setFormError(null);
      setNotice(null);
      try {
        if (draft.channelId) {
          const patch: UpdateChannelInput = {
            name: draft.name.trim(),
            eventPolicy: draft.eventPolicy,
          };
          // Only re-send the connection when the topic was re-entered (secrets are write-only).
          if (draft.topic.trim()) {
            patch.ntfy = {
              baseUrl: draft.baseUrl.trim(),
              topic: draft.topic.trim(),
              ...(draft.token.trim() ? { token: draft.token.trim() } : {}),
            };
          }
          const updated = await client.updateChannel(draft.channelId, patch);
          setChannels((prev) =>
            prev.map((c) => (c.channelId === updated.channelId ? updated : c)),
          );
          setNotice(`Saved “${updated.name}”.`);
        } else {
          const input: CreateChannelInput = {
            name: draft.name.trim(),
            provider: 'ntfy',
            ntfy: {
              baseUrl: draft.baseUrl.trim(),
              topic: draft.topic.trim(),
              ...(draft.token.trim() ? { token: draft.token.trim() } : {}),
            },
            eventPolicy: draft.eventPolicy,
          };
          const created = await client.createChannel(artistId, input);
          setChannels((prev) => [created, ...prev]);
          setNotice(`Added “${created.name}”.`);
        }
        // Clear the form — crucially wiping the secret topic/token from state (§21.2).
        resetForm();
      } catch (err) {
        if (err instanceof ApiError) setFormError(err);
        else setFormError(null);
      } finally {
        setSaving(false);
      }
    },
    [client, artistId, draft, resetForm],
  );

  const onSendTest = useCallback(
    async (channel: NotificationChannel) => {
      setBusyChannelId(channel.channelId);
      setNotice(null);
      try {
        const result = await client.testChannel(channel.channelId);
        setNotice(`Test sent to “${channel.name}” — delivery ${result.status}.`);
        void loadChannels();
        void loadDeliveries();
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : 'Could not send test.');
      } finally {
        setBusyChannelId(null);
      }
    },
    [client, loadChannels, loadDeliveries],
  );

  const onToggleEnabled = useCallback(
    async (channel: NotificationChannel) => {
      const next = channel.status === 'disabled' ? 'ready' : 'disabled';
      setBusyChannelId(channel.channelId);
      setNotice(null);
      try {
        const updated = await client.updateChannel(channel.channelId, { status: next });
        setChannels((prev) =>
          prev.map((c) => (c.channelId === updated.channelId ? updated : c)),
        );
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : 'Could not update channel.');
      } finally {
        setBusyChannelId(null);
      }
    },
    [client],
  );

  const onReplay = useCallback(
    async (delivery: NotificationDelivery) => {
      setBusyDeliveryId(delivery.deliveryId);
      setNotice(null);
      try {
        const result = await client.replayDelivery(delivery.deliveryId);
        setNotice(`Replayed delivery — ${result.status}.`);
        void loadDeliveries();
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : 'Could not replay delivery.');
      } finally {
        setBusyDeliveryId(null);
      }
    },
    [client, loadDeliveries],
  );

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Integrations
          </p>
          <h1>Integrations</h1>
          <p className="muted">ntfy notification channels and delivery health for this artist.</p>
        </div>
      </header>

      {loadError && <p className="error">{loadError}</p>}

      <section className="card">
        <div className="module-head">
          <h2>{draft.channelId ? 'Edit channel' : 'Add an ntfy channel'}</h2>
          {notice && (
            <span className="muted" aria-live="polite">
              {notice}
            </span>
          )}
        </div>
        <form className="channel-form" onSubmit={onSubmit}>
          <label>
            Name
            <input
              value={draft.name}
              required
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Stage phone"
            />
          </label>
          <label>
            Base URL
            <input
              value={draft.baseUrl}
              onChange={(e) => setDraft((d) => ({ ...d, baseUrl: e.target.value }))}
              placeholder="https://ntfy.sh"
              spellCheck={false}
              autoCapitalize="none"
            />
          </label>
          <label>
            Topic
            <input
              value={draft.topic}
              onChange={(e) => setDraft((d) => ({ ...d, topic: e.target.value }))}
              placeholder={draft.channelId ? 'Re-enter to change topic' : 'unguessable-topic-name'}
              spellCheck={false}
              autoCapitalize="none"
              autoComplete="off"
            />
          </label>
          <label>
            Access token (optional)
            <input
              type="password"
              value={draft.token}
              onChange={(e) => setDraft((d) => ({ ...d, token: e.target.value }))}
              placeholder="tk_…"
              spellCheck={false}
              autoCapitalize="none"
              autoComplete="off"
            />
          </label>
          <p className="help channel-form__note">
            The topic and token are stored securely and are never shown again. Leave them blank when
            editing to keep the existing connection.
          </p>
          <fieldset className="channel-form__events">
            <legend>Notify on</legend>
            {EVENT_TYPES.map((evt) => (
              <label key={evt.key} className="checkbox">
                <input
                  type="checkbox"
                  checked={draft.eventPolicy[evt.key] ?? false}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      eventPolicy: { ...d.eventPolicy, [evt.key]: e.target.checked },
                    }))
                  }
                />
                {evt.label}
              </label>
            ))}
          </fieldset>
          <div className="button-row">
            <button
              type="submit"
              className="action--primary"
              disabled={saving || !draft.name.trim()}
            >
              {draft.channelId ? 'Save changes' : 'Add channel'}
            </button>
            {draft.channelId && (
              <button type="button" className="ghost" onClick={resetForm} disabled={saving}>
                Cancel edit
              </button>
            )}
          </div>
          {formError && (
            <div className="validation" role="alert">
              <p className="error">{formError.message}</p>
              {formError.fieldErrors.length > 0 && (
                <ul>
                  {formError.fieldErrors.map((fe, i) => (
                    <li key={`${fe.path}-${i}`}>
                      <code>{fe.path}</code>: {fe.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </section>

      <section className="card">
        <h2>Channels</h2>
        {channels.length === 0 && !loading ? (
          <p className="muted">No channels yet. Add an ntfy channel above.</p>
        ) : (
          <ul className="channel-list">
            {channels.map((channel) => (
              <li key={channel.channelId} className="channel-card">
                <div className="channel-card__head">
                  <div>
                    <h3 className="channel-card__name">{channel.name}</h3>
                    <p className="muted channel-card__meta">
                      {channel.provider}
                      {channel.safeConfig.host ? ` · ${channel.safeConfig.host}` : ''}
                      {channel.safeConfig.topicFingerprint
                        ? ` · topic ${channel.safeConfig.topicFingerprint}`
                        : ''}
                    </p>
                  </div>
                  <span className={`health-badge health-badge--${channel.status}`}>
                    {channel.status}
                  </span>
                </div>

                <dl className="channel-health">
                  <div>
                    <dt>Last success</dt>
                    <dd>{formatTime(channel.lastSuccessAt)}</dd>
                  </div>
                  <div>
                    <dt>Last test</dt>
                    <dd>{formatTime(channel.lastTestAt)}</dd>
                  </div>
                  <div>
                    <dt>Last failure</dt>
                    <dd>
                      {channel.lastFailure
                        ? `${formatTime(channel.lastFailure.at)} · ${channel.lastFailure.code}`
                        : '—'}
                    </dd>
                  </div>
                </dl>

                <div className="button-row">
                  <button
                    type="button"
                    onClick={() => void onSendTest(channel)}
                    disabled={busyChannelId === channel.channelId}
                  >
                    Send test
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => editChannel(channel)}
                    disabled={busyChannelId === channel.channelId}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={channel.status === 'disabled' ? 'action--primary' : 'ghost'}
                    onClick={() => void onToggleEnabled(channel)}
                    disabled={busyChannelId === channel.channelId}
                  >
                    {channel.status === 'disabled' ? 'Enable' : 'Disable'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <p aria-busy="true">Loading…</p>}
      </section>

      <section className="card">
        <div className="module-head">
          <h2>Deliveries</h2>
          <button type="button" className="link" onClick={() => void loadDeliveries()}>
            Refresh
          </button>
        </div>
        {deliveries.length === 0 ? (
          <p className="muted">No deliveries yet.</p>
        ) : (
          <div className="deliveries-scroll">
            <table className="deliveries-table">
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Response</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((delivery) => (
                  <tr key={delivery.deliveryId}>
                    <td>{delivery.eventType}</td>
                    <td>
                      <span className={`status-badge status-badge--${delivery.status}`}>
                        {DELIVERY_STATUS_LABEL[delivery.status]}
                      </span>
                    </td>
                    <td>{delivery.attemptCount}</td>
                    <td>{delivery.providerResponseCode ?? '—'}</td>
                    <td>{formatTime(delivery.createdAt)}</td>
                    <td>{formatTime(delivery.updatedAt)}</td>
                    <td>
                      {isReplayable(delivery.status) && (
                        <button
                          type="button"
                          onClick={() => void onReplay(delivery)}
                          disabled={busyDeliveryId === delivery.deliveryId}
                        >
                          Replay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
